import check from "./util/check";
import isTypedArray from "./util/is-typed-array";
import isNDArrayLike from "./util/is-ndarray";
import values from "./util/values";

import primTypes from "./constants/primitives.json";
import usageTypes from "./constants/usage.json";

const { raise, type: _type, parameter } = check;

const GL_POINTS = 0;
const GL_LINES = 1;
const GL_TRIANGLES = 4;

const GL_BYTE = 5120;
const GL_UNSIGNED_BYTE = 5121;
const GL_SHORT = 5122;
const GL_UNSIGNED_SHORT = 5123;
const GL_INT = 5124;
const GL_UNSIGNED_INT = 5125;

const GL_ELEMENT_ARRAY_BUFFER = 34963;

const GL_STREAM_DRAW = 0x88e0;
const GL_STATIC_DRAW = 0x88e4;

export default function wrapElementsState(gl, extensions, bufferState, stats) {
  const elementSet = {};
  let elementCount = 0;

  const elementTypes = {
    uint8: GL_UNSIGNED_BYTE,
    uint16: GL_UNSIGNED_SHORT,
  };

  if (extensions.oes_element_index_uint) {
    elementTypes.uint32 = GL_UNSIGNED_INT;
  }

  function REGLElementBuffer(buffer) {
    this.id = elementCount++;
    elementSet[this.id] = this;
    this.buffer = buffer;
    this.primType = GL_TRIANGLES;
    this.vertCount = 0;
    this.type = 0;
  }

  REGLElementBuffer.prototype.bind = function() {
    this.buffer.bind();
  };

  const bufferPool = [];

  function createElementStream(data) {
    let result = bufferPool.pop();
    if (!result) {
      result = new REGLElementBuffer(
        bufferState.create(null, GL_ELEMENT_ARRAY_BUFFER, true, false)._buffer
      );
    }
    initElements(result, data, GL_STREAM_DRAW, -1, -1, 0, 0);
    return result;
  }

  function destroyElementStream(elements) {
    bufferPool.push(elements);
  }

  function initElements(elements, data, usage, prim, count, byteLength, type) {
    elements.buffer.bind();
    let dtype;
    if (data) {
      let predictedType = type;
      if (
        !type &&
        (!isTypedArray(data) ||
          (isNDArrayLike(data) && !isTypedArray(data.data)))
      ) {
        predictedType = extensions.oes_element_index_uint
          ? GL_UNSIGNED_INT
          : GL_UNSIGNED_SHORT;
      }
      bufferState._initBuffer(elements.buffer, data, usage, predictedType, 3);
    } else {
      gl.bufferData(GL_ELEMENT_ARRAY_BUFFER, byteLength, usage);
      elements.buffer.dtype = dtype || GL_UNSIGNED_BYTE;
      elements.buffer.usage = usage;
      elements.buffer.dimension = 3;
      elements.buffer.byteLength = byteLength;
    }

    dtype = type;
    if (!type) {
      switch (elements.buffer.dtype) {
        case GL_UNSIGNED_BYTE:
        case GL_BYTE:
          dtype = GL_UNSIGNED_BYTE;
          break;

        case GL_UNSIGNED_SHORT:
        case GL_SHORT:
          dtype = GL_UNSIGNED_SHORT;
          break;

        case GL_UNSIGNED_INT:
        case GL_INT:
          dtype = GL_UNSIGNED_INT;
          break;

        default:
          raise("unsupported type for element array");
      }
      elements.buffer.dtype = dtype;
    }
    elements.type = dtype;

    // Check oes_element_index_uint extension
    check(
      dtype !== GL_UNSIGNED_INT || !!extensions.oes_element_index_uint,
      "32 bit element buffers not supported, enable oes_element_index_uint first"
    );

    // try to guess default primitive type and arguments
    let vertCount = count;
    if (vertCount < 0) {
      vertCount = elements.buffer.byteLength;
      if (dtype === GL_UNSIGNED_SHORT) {
        vertCount >>= 1;
      } else if (dtype === GL_UNSIGNED_INT) {
        vertCount >>= 2;
      }
    }
    elements.vertCount = vertCount;

    // try to guess primitive type from cell dimension
    let primType = prim;
    if (prim < 0) {
      primType = GL_TRIANGLES;
      const dimension = elements.buffer.dimension;
      if (dimension === 1) primType = GL_POINTS;
      if (dimension === 2) primType = GL_LINES;
      if (dimension === 3) primType = GL_TRIANGLES;
    }
    elements.primType = primType;
  }

  function destroyElements(elements) {
    stats.elementsCount--;

    check(elements.buffer !== null, "must not double destroy elements");
    delete elementSet[elements.id];
    elements.buffer.destroy();
    elements.buffer = null;
  }

  function createElements(options, persistent) {
    const buffer = bufferState.create(null, GL_ELEMENT_ARRAY_BUFFER, true);
    const elements = new REGLElementBuffer(buffer._buffer);
    stats.elementsCount++;

    function reglElements(options) {
      if (!options) {
        buffer();
        elements.primType = GL_TRIANGLES;
        elements.vertCount = 0;
        elements.type = GL_UNSIGNED_BYTE;
      } else if (typeof options === "number") {
        buffer(options);
        elements.primType = GL_TRIANGLES;
        elements.vertCount = options | 0;
        elements.type = GL_UNSIGNED_BYTE;
      } else {
        let data = null;
        let usage = GL_STATIC_DRAW;
        let primType = -1;
        let vertCount = -1;
        let byteLength = 0;
        let dtype = 0;
        if (
          Array.isArray(options) ||
          isTypedArray(options) ||
          isNDArrayLike(options)
        ) {
          data = options;
        } else {
          _type(options, "object", "invalid arguments for elements");
          if ("data" in options) {
            data = options.data;
            check(
              Array.isArray(data) || isTypedArray(data) || isNDArrayLike(data),
              "invalid data for element buffer"
            );
          }
          if ("usage" in options) {
            parameter(
              options.usage,
              usageTypes,
              "invalid element buffer usage"
            );
            usage = usageTypes[options.usage];
          }
          if ("primitive" in options) {
            parameter(
              options.primitive,
              primTypes,
              "invalid element buffer primitive"
            );
            primType = primTypes[options.primitive];
          }
          if ("count" in options) {
            check(
              typeof options.count === "number" && options.count >= 0,
              "invalid vertex count for elements"
            );
            vertCount = options.count | 0;
          }
          if ("type" in options) {
            parameter(options.type, elementTypes, "invalid buffer type");
            dtype = elementTypes[options.type];
          }
          if ("length" in options) {
            byteLength = options.length | 0;
          } else {
            byteLength = vertCount;
            if (dtype === GL_UNSIGNED_SHORT || dtype === GL_SHORT) {
              byteLength *= 2;
            } else if (dtype === GL_UNSIGNED_INT || dtype === GL_INT) {
              byteLength *= 4;
            }
          }
        }
        initElements(
          elements,
          data,
          usage,
          primType,
          vertCount,
          byteLength,
          dtype
        );
      }

      return reglElements;
    }

    reglElements(options);

    reglElements._reglType = "elements";
    reglElements._elements = elements;
    reglElements.subdata = function(data, offset) {
      buffer.subdata(data, offset);
      return reglElements;
    };
    reglElements.destroy = function() {
      destroyElements(elements);
    };

    return reglElements;
  }

  return {
    create: createElements,
    createStream: createElementStream,
    destroyStream: destroyElementStream,
    getElements: function(elements) {
      if (
        typeof elements === "function" &&
        elements._elements instanceof REGLElementBuffer
      ) {
        return elements._elements;
      }
      return null;
    },
    clear: function() {
      values(elementSet).forEach(destroyElements);
    },
  };
}
